import type { ServiceItem } from '../types';
import type { FlatInclusionDecision } from '../services/flatRateLogic';
import {
    countsTowardInspectionCounter,
    isFlatRatePosition,
    ownReportProofCount,
    type InspectionProofLogLike,
    type InspectionProofServiceLike,
} from '../services/projectLv';
import { hatBestandteile, normalisiereBestandteile } from '../services/bestandteile/bestandteilLogic';

/**
 * Rechenkern des Dialogs „Nachtrag & Daten-Korrektur" (ProjectSupplementModal).
 *
 * Rein — kein React, kein Netz: das Projekt hat keine DOM-Testumgebung, und
 * genau hier entscheidet sich, ob beim Speichern eine Korrekturbuchung (Geld)
 * und ein neuer Begehungszaehler (Berichtsnummern) entstehen.
 *
 * Seit 30.09.2026 bucht der Versand im Pauschalprojekt jede Begehung als
 * NACHWEISZEILE auf die Pauschale (Menge 0, 'NW-…'); die Begehungsposition
 * waechst nicht mehr. Der Zaehler (current_inspection_number) zaehlt beides.
 * Alles hier trennt deshalb den Anteil der Position vom Anteil der Nachweise —
 * und zwar nur der Nachweise EIGENER Berichte (supplementProofCount).
 */

/** Eine Veraenderung am IST-Stand — daraus wird der datierte Korrektureintrag. */
export interface SupplementUsedDelta {
    serviceId: string;
    title: string;
    delta: number;
    from: number;
    to: number;
}

export interface SupplementDiff {
    /** Aenderungsvermerk (Projektnotiz); leer = nichts zu speichern. */
    changes: string[];
    /** Summe der IST-Staende der Begehungspositionen — OHNE die Nachweise auf der Pauschale. */
    usedInspections: number;
    /** Hat sich diese Summe gegenueber dem Stand beim Oeffnen bewegt? */
    needsCounterSync: boolean;
    usedDeltas: SupplementUsedDelta[];
}

/**
 * Der Anteil des Zaehlers, der als Nachweis auf der Pauschale steht: nur
 * Nachweise EIGENER Berichte, denn nur sie haben eine Nummer verbraucht.
 *
 * Eine Hand-Begehung ('NW-BEG-…') oder ein Tochterbericht ('RPT-CHILD-…') hat
 * den Zaehler dieses Projekts nie bewegt. Mitgezaehlt (Anzeige-Zahl
 * inspectionProofCount) zogen sie 2.7.10 beim Oeffnen unter die echten
 * Buchungen — 55 -> 53 —, und jedes Speichern schrieb −2 als Korrektur.
 * Oeffnen UND Speichern (ProjectSupplementModal) gehen durch diese eine
 * Funktion, damit beide denselben Anteil meinen.
 */
export const supplementProofCount = (
    services: ReadonlyArray<InspectionProofServiceLike | null | undefined> | null | undefined,
    serviceLog: ReadonlyArray<InspectionProofLogLike | null | undefined> | null | undefined,
): number => ownReportProofCount(services, serviceLog);

/** Summe der IST-Staende aller Positionen, die in den Begehungszaehler zaehlen. */
export const supplementUsedInspections = (services: ServiceItem[]): number =>
    services.reduce((sum, s) => sum + (countsTowardInspectionCounter(s) ? (s.quantityUsed || 0) : 0), 0);

/**
 * Der Stand, mit dem der Dialog oeffnet: die Positionen, wie sie sind — OHNE
 * Abgleich mit dem Berichtszaehler (seit 18.10.2026).
 *
 * Bis dahin setzte der Dialog die Begehungsposition beim Oeffnen auf den
 * Live-Zaehler (applyInspectionCounter), und JEDES Speichern schrieb die
 * Differenz als „Korrektur des Leistungsstands" ins Protokoll, egal was der
 * Bediener geaendert hatte (Preis, Vertragsmenge, neue Position). Weicht
 * erbracht absichtlich vom Zaehler ab (Altbestand, gewollte Korrektur), traf
 * das Geld: 5805 (erbracht 42, Zaehler 41) verlor am 07.10. zweimal eine
 * Begehung, 6070-3 (erbracht 97, Berichte bis #100) haette drei Begehungen
 * doppelt berechnet. Der Zaehler ist nicht mehr die Quelle des IST-Stands —
 * jeder Versand bucht seine Zeile selbst; eine echte Luecke meldet der
 * Nachtlauf, nachgebucht wird sie mit Berichtsbezug.
 *
 * `counterFollows`: gleicht ein Nachtrag den Zaehler an die Positionen an?
 * Nur wenn beide beim Oeffnen zusammenpassen (Zaehler = Positionen +
 * Nachweise, supplementInspectionNr) oder der Zaehler nie gepflegt wurde (0,
 * Neuanlage mit Altbestand). Weichen sie ab, bleibt der Zaehler stehen: die
 * Summe der Positionen zurueckzuschreiben setzte 6070-3 bei einer
 * Handkorrektur 97 -> 98 von 100 auf 98, und die naechsten Berichte bekaemen
 * Nummern, die es schon gibt.
 *
 * `proofCount`: Nachweise eigener Berichte auf der Pauschale
 * (supplementProofCount) — der Anteil des Zaehlers, der nicht auf der
 * Position steht.
 */
export const prepareSupplementServices = (
    services: ServiceItem[],
    liveCount: number,
    serviceLog: ReadonlyArray<InspectionProofLogLike | null | undefined> | null | undefined,
): { services: ServiceItem[]; proofCount: number; usedInspections: number; counterFollows: boolean } => {
    const proofCount = supplementProofCount(services, serviceLog);
    const usedInspections = supplementUsedInspections(services);
    const counterFollows = !(liveCount > 0) || liveCount === supplementInspectionNr(usedInspections, proofCount);
    return { services, proofCount, usedInspections, counterFollows };
};

const bestandteilZahl = (s: Pick<ServiceItem, 'bestandteile'>): number => normalisiereBestandteile(s?.bestandteile)?.length ?? 0;

/**
 * Hat sich die Bestandteil-Definition einer Position geaendert? Normalisiert
 * verglichen (Reihenfolge zaehlt, sie ist Teil der Definition).
 */
export const bestandteileGeaendert = (
    alt: Pick<ServiceItem, 'bestandteile'> | null | undefined,
    neu: Pick<ServiceItem, 'bestandteile'> | null | undefined,
): boolean =>
    JSON.stringify(normalisiereBestandteile(alt?.bestandteile)) !== JSON.stringify(normalisiereBestandteile(neu?.bestandteile));

/**
 * LOESCHSPERRE fuer Paketpositionen (Befund A3).
 *
 * Der Dialog loescht eine Position sofort in `project_services`. Die
 * Datenbank-Kaskade nimmt dabei die Bestandteil-Eintraege mit (Datum,
 * Uhrzeit, Person — der ganze Nachweis). Eine Paketposition, die gebucht ist
 * oder erledigte Bestandteile hat, darf deshalb hier nicht direkt geloescht
 * werden.
 *
 * Ob Eintraege existieren, steht ohne Netz an `bestandteileOffen` (von der
 * Datenbank gepflegt): weniger offen als definiert = es gibt erledigte bzw.
 * entfallene. Ist der Wert unbekannt, wird gesperrt (fail closed).
 *
 * Geprueft wird die Position, wie sie beim OEFFNEN geladen war — gegen deren
 * Definition rechnet die Datenbank, nicht gegen eine im Dialog geaenderte.
 * `null` = Loeschen erlaubt (keine Paketposition, oder neu im Dialog).
 */
export const paketLoeschSperre = (geladen: ServiceItem | null | undefined): string | null => {
    if (!geladen || !hatBestandteile(geladen)) return null;
    const name = `„${geladen.title || geladen.lvPosition || 'Position'}“`;
    const grund = 'Beim Löschen gingen die erledigten Bestandteile mit Datum, Uhrzeit und Person verloren.';
    if ((Number(geladen.quantityUsed) || 0) > 0 || (Number(geladen.quantityBilled) || 0) > 0) {
        return `${name} ist eine Paketposition mit Bestandteilen und bereits gebucht. Sie kann hier nicht gelöscht werden. ${grund}`;
    }
    const offen = geladen.bestandteileOffen;
    if (typeof offen !== 'number' || !Number.isFinite(offen) || offen < 0) {
        return `${name} ist eine Paketposition mit Bestandteilen, deren Stand nicht geladen werden konnte. Sie kann hier nicht gelöscht werden. ${grund}`;
    }
    // `[]` (A3, Codex R3): ein Paket aus lauter UNLESBAREN Bestandteilen. Die
    // Datenbank zaehlt mindestens einen; ob dazu Eintraege existieren, ist hier
    // nicht zu sehen (bei offen 0 sogar sicher) — also gesperrt.
    if (bestandteilZahl(geladen) === 0) {
        return `${name} ist eine Paketposition, deren Bestandteile diese App-Version nicht lesen kann. Sie kann hier nicht gelöscht werden. ${grund}`;
    }
    if (offen < bestandteilZahl(geladen)) {
        return `${name} ist eine Paketposition mit bereits erledigten Bestandteilen. Sie kann hier nicht gelöscht werden. ${grund}`;
    }
    return null;
};

/**
 * Welche Bestandteile haben (vermutlich) einen Eintrag? Fuer die Rueckfrage
 * beim Entfernen im Editor — ohne die Eintraege zu laden, allein aus
 * `bestandteileOffen` der beim Oeffnen geladenen Position:
 *  - nichts erledigt (offen = Zahl der Bestandteile): keine Keys;
 *  - alles erledigt (offen 0): alle Keys, sicher;
 *  - teilweise oder Stand unbekannt: alle Keys, aber nur VERMUTET — welche
 *    erledigt sind, weiss nur der Positionsdialog (Paket D), der die
 *    Eintraege laedt.
 */
export const vermuteteEintragsKeys = (
    geladen: ServiceItem | null | undefined,
): { keys: string[]; vermutet: boolean } => {
    const keys = (normalisiereBestandteile(geladen?.bestandteile) ?? []).map((b) => b.key);
    if (keys.length === 0) return { keys: [], vermutet: false };
    const offen = geladen?.bestandteileOffen;
    const bekannt = typeof offen === 'number' && Number.isFinite(offen) && offen >= 0;
    if (bekannt && offen >= keys.length) return { keys: [], vermutet: false };
    if (bekannt && offen === 0) return { keys, vermutet: false };
    return { keys, vermutet: true };
};

/**
 * Was hat sich gegenueber dem Stand beim Oeffnen geaendert?
 *
 * Unveraendert aus dem Dialog herausgezogen; neu ist nur, dass die Summe der
 * Begehungspositionen (`usedInspections`) nicht mehr selbst der neue Zaehler
 * ist — den bildet supplementInspectionNr mit den Nachweisen.
 */
export const diffSupplementServices = (
    services: ServiceItem[],
    initialServices: ServiceItem[],
    flatDecisions: Record<string, FlatInclusionDecision>,
): SupplementDiff => {
    const log: string[] = [];
    // Veraenderungen am IST-Stand getrennt sammeln: aus ihnen entsteht der
    // datierte Protokolleintrag, ohne den die Menge in keiner
    // Zeitraumabrechnung auftaucht.
    const deltas: SupplementUsedDelta[] = [];
    const origMap = new Map<string, ServiceItem>(initialServices.map(s => [String(s.id), s]));
    let totalUsedInspections = 0;
    let originalUsedInspections = 0;

    services.forEach(newS => {
        const oldS = origMap.get(newS.id);
        // Und umgekehrt: eine Stundenposition darf nicht in die Summe
        // eingehen, die als neuer Begehungszaehler zurueckgeschrieben wird.
        const isInsp = countsTowardInspectionCounter(newS);
        if (isInsp) totalUsedInspections += (newS.quantityUsed || 0);

        if (!oldS) {
            log.push(`Neu: "${newS.title}"`);
            // Die Pauschal-Entscheidung gehoert in den Aenderungsvermerk:
            // sie veraendert, was die Position kuenftig KOSTET, und muss
            // spaeter nachlesbar sein wie jede Preisaenderung.
            const decision = flatDecisions[newS.id];
            if (decision) {
                log.push(`Monatspauschale "${newS.title}": ${decision === 'included' ? 'enthalten' : 'nicht enthalten'}`);
            }
            // Auch eine neue Position kann mit IST-Stand angelegt werden.
            if ((newS.quantityUsed || 0) !== 0) {
                deltas.push({ serviceId: String(newS.id), title: newS.title, delta: newS.quantityUsed || 0, from: 0, to: newS.quantityUsed || 0 });
            }
        } else {
            if (isInsp) originalUsedInspections += (oldS.quantityUsed || 0);
            if (newS.quantityContracted !== oldS.quantityContracted) log.push(`Vertrag "${newS.title}": ${oldS.quantityContracted} -> ${newS.quantityContracted}`);
            if (newS.quantityUsed !== oldS.quantityUsed) {
                log.push(`IST-Stand "${newS.title}": ${oldS.quantityUsed} -> ${newS.quantityUsed} (Manuelle Korrektur)`);
                deltas.push({
                    serviceId: String(newS.id),
                    title: newS.title,
                    // Die Differenz, nicht der neue Stand: das Protokoll
                    // fuehrt Bewegungen. Eine Korrektur nach unten ergibt
                    // einen negativen Eintrag — genau wie beim Storno, und
                    // die Summe der Bewegungen bleibt der IST-Stand.
                    delta: (newS.quantityUsed || 0) - (oldS.quantityUsed || 0),
                    from: oldS.quantityUsed || 0,
                    to: newS.quantityUsed || 0,
                });
            }
            if (newS.unitPrice !== oldS.unitPrice) log.push(`Preis "${newS.title}": ${oldS.unitPrice}€ -> ${newS.unitPrice}€`);
            // Ohne eigenen Protokolleintrag bliebe der Speichern-Knopf
            // gesperrt (er haengt an changes.length) und eine reine
            // Mindeststunden-Aenderung waere nicht zu sichern.
            if ((newS.minHours ?? null) !== (oldS.minHours ?? null)) {
                log.push(`Mindeststunden "${newS.title}": ${oldS.minHours ?? '—'} -> ${newS.minHours ?? '—'}`);
            }
            // Paketposition: auch eine reine Aenderung der Bestandteile muss
            // speicherbar sein (sonst bleibt der Knopf gesperrt) und im
            // Vermerk stehen. Verglichen wird die NORMALISIERTE Liste —
            // `undefined`, `null` und `[]` heissen alle „keine Bestandteile".
            if (bestandteileGeaendert(oldS, newS)) {
                log.push(`Bestandteile "${newS.title}": ${bestandteilZahl(oldS)} -> ${bestandteilZahl(newS)}`);
            }
            origMap.delete(newS.id);
        }
    });
    return {
        changes: log,
        usedInspections: totalUsedInspections,
        needsCounterSync: totalUsedInspections !== originalUsedInspections,
        usedDeltas: deltas,
    };
};

/**
 * Der Begehungszaehler, den ein Nachtrag zurueckschreibt: Stand der
 * Begehungspositionen PLUS Nachweise eigener Berichte auf der Pauschale
 * (supplementProofCount — Hand- und Tochternachweise hoben ihn sonst ueber die
 * vergebenen Nummern, und die naechsten Berichte uebersprangen Nummern).
 *
 * Nur die Positionen zurueckzuschreiben setzte den Zaehler im Pauschalprojekt
 * bei jeder Aenderung an einer 2.7.x-Position auf deren Stand zurueck (5633:
 * von 61 auf 55) — die naechsten sechs Berichte bekaemen Nummern, die es schon
 * gibt.
 *
 * Mehrere Zaehlstaende (beim Oeffnen, beim Speichern): der groessere gilt.
 * Geht waehrend des offenen Dialogs ein Bericht raus, steht sein Nachweis nur
 * im frischen Protokoll; ein Zaehler eins zu hoch laesst eine Nummer aus, einer
 * eins zu niedrig vergaebe sie zweimal.
 */
export const supplementInspectionNr = (usedInspections: number, ...proofCounts: number[]): number =>
    usedInspections + Math.max(0, ...proofCounts.filter(n => Number.isFinite(n)));

/**
 * Der Zaehler, den das Speichern zurueckschreibt — oder `null`: Zaehler
 * bleibt, wie er ist. Zurueckgeschrieben wird nur, wenn sich die
 * Begehungssumme bewegt hat UND der Zaehler beim Oeffnen zu den Positionen
 * passte (counterFollows, prepareSupplementServices).
 *
 * Nie unter `currentCounter`, den beim Speichern FRISCH gelesenen Zaehler:
 * jede Nummer bis dorthin ist vergeben. Eine Korrektur nach unten (doppelt
 * gebuchte Begehung) oder ein waehrend des offenen Dialogs versendeter
 * Bericht setzte ihn sonst zurueck, und die naechsten Berichte bekaemen
 * Nummern, die es schon gibt.
 */
export const supplementNewCounter = (
    diff: Pick<SupplementDiff, 'usedInspections' | 'needsCounterSync'>,
    opts: { counterFollows: boolean; currentCounter: number; proofCounts: number[] },
): number | null => {
    if (!diff.needsCounterSync || !opts.counterFollows) return null;
    const ausPositionen = supplementInspectionNr(diff.usedInspections, ...opts.proofCounts);
    return Math.max(ausPositionen, Number.isFinite(opts.currentCounter) ? opts.currentCounter : 0);
};

const rundeMenge = (n: number): number => Math.round(n * 1000) / 1000;

const gleicherWert = (a: unknown, b: unknown): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

/** Kennung wie beim Oeffnen des Dialogs: ohne wahre id (fehlt, leer, 0) keine. */
const kennungVon = (s: ServiceItem | null | undefined): string | null => (s?.id ? String(s.id) : null);

/** Der frische Stand laesst sich nicht sicher mit dem Dialog abgleichen — nicht speichern. */
export class NachtragKonflikt extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'NachtragKonflikt';
    }
}

/**
 * Legt die Aenderungen des Dialogs auf den beim Speichern FRISCH geladenen
 * Stand (Anker-Muster wie beim Sync).
 *
 * Der Dialog hielt die Positionen vom Oeffnen und schrieb sie beim Speichern
 * zurueck. Ging in der Zwischenzeit ein Bericht raus oder wurde ein Beleg
 * ausgestellt, ueberschrieb das dessen Stand: der Bericht stand im Protokoll,
 * die Position eine Begehung tiefer, `quantityBilled` auf dem alten Wert.
 *
 * Je Position: Grundlage ist der frische Stand; uebernommen wird nur, was der
 * Bediener im Dialog geaendert hat (Vergleich mit dem Stand beim Oeffnen). Der
 * IST-Stand geht als DIFFERENZ ein — eine Korrektur 41 -> 42 auf einer
 * inzwischen auf 42 gewachsenen Position ergibt 43, der neue Bericht bleibt.
 * Neue Positionen des Dialogs kommen unveraendert, inzwischen anderswo
 * angelegte bleiben erhalten, anderswo geloeschte bleiben geloescht (hat der
 * Bediener sie geaendert: NachtragKonflikt).
 */
export const rebaseSupplementServices = (
    edited: ServiceItem[],
    initial: ServiceItem[],
    fresh: ServiceItem[],
): ServiceItem[] => {
    // Die Positionen kommen aus project_services (uuid-Primaerschluessel) und
    // tragen immer eine Kennung. Fehlt sie doch, ist nicht sicher zu sagen,
    // welche Position welche ist — lieber nicht speichern als eine Menge auf
    // die falsche Position legen (Codex R3: Zuordnung nach Reihenfolge
    // vertauscht bei Entfernen/Umsortieren).
    if (fresh.some(f => kennungVon(f) === null)) {
        throw new NachtragKonflikt(
            'Im Leistungsverzeichnis steht eine Position ohne Kennung. Der Nachtrag wurde nicht gespeichert, '
            + 'damit keine Menge auf der falschen Position landet. Bitte den Dialog neu öffnen; bleibt der Fehler, bitte melden.',
        );
    }
    const initialById = new Map(initial.map(s => [String(s.id), s]));
    const freshById = new Map(fresh.map(f => [String(f.id), f]));
    const editedIds = new Set(edited.map(s => String(s.id)));

    const rebased: ServiceItem[] = [];
    for (const s of edited) {
        const anfang = initialById.get(String(s.id));
        // Neu im Dialog angelegt: so, wie sie ist.
        if (!anfang) {
            rebased.push(s);
            continue;
        }
        const geaendert: Partial<ServiceItem> = {};
        const felder = new Set([...Object.keys(anfang), ...Object.keys(s)]) as Set<keyof ServiceItem>;
        felder.forEach(feld => {
            if (feld === 'id' || feld === 'quantityUsed') return;
            if (!gleicherWert(s[feld], anfang[feld])) Object.assign(geaendert, { [feld]: s[feld] });
        });
        const delta = (s.quantityUsed || 0) - (anfang.quantityUsed || 0);
        const frisch = freshById.get(String(s.id));
        // Inzwischen anderswo geloescht: unveraendert bleibt sie geloescht
        // (frueher legte der alte Dialogstand sie wieder an). Hat der Bediener
        // sie geaendert, ist nicht zu entscheiden, was gilt — nicht speichern.
        if (!frisch) {
            if (Object.keys(geaendert).length > 0 || delta !== 0) {
                throw new NachtragKonflikt(
                    `Die Position „${s.title || s.lvPosition || s.id}“ wurde inzwischen gelöscht. Der Nachtrag wurde nicht gespeichert. `
                    + 'Bitte den Dialog neu öffnen und die Änderung dort noch einmal eintragen.',
                );
            }
            continue;
        }
        rebased.push({ ...frisch, ...geaendert, id: s.id, quantityUsed: rundeMenge((frisch.quantityUsed || 0) + delta) });
    }

    const anderswoAngelegt = fresh.filter(f => {
        const id = kennungVon(f);
        return id !== null && !editedIds.has(id) && !initialById.has(id);
    });
    return [...rebased, ...anderswoAngelegt];
};

/**
 * Die Korrekturbewegungen mit dem frischen Ausgangsstand beschriftet: die
 * Menge bleibt die Differenz des Bedieners, „von → nach" nennt den Stand, auf
 * den sie tatsaechlich gebucht wird (rebaseSupplementServices).
 */
export const rebaseUsedDeltas = (deltas: SupplementUsedDelta[], fresh: ServiceItem[]): SupplementUsedDelta[] => {
    return deltas.map(d => {
        const frisch = fresh.find(f => kennungVon(f) === d.serviceId);
        if (!frisch) return d;
        const from = frisch.quantityUsed || 0;
        return { ...d, from, to: rundeMenge(from + d.delta) };
    });
};

/**
 * Die Position, deren Vertragsmenge das Projektende hochrechnet.
 *
 * Eine Monatspauschale nie: ihre Vertragsmenge sind MONATE. Ueber Titel
 * („Begehung") oder LV-Nummer (2.7.) geriet sie sonst in die Auswahl, und
 * zwoelf Monate gegen zehn Begehungen machten sie zur fuehrenden Position —
 * das Enddatum stuende dann auf zwoelf Turnusterminen statt zehn.
 */
export const supplementLeadingService = (services: ServiceItem[]): ServiceItem | null => {
    const inspectionServices = services.filter(s =>
        !isFlatRatePosition(s) &&
        (s.assignment === 'inspection' ||
            (s.lvPosition && (s.lvPosition.startsWith('2.7.') || s.lvPosition.startsWith('2.8.'))) ||
            (s.title && s.title.toLowerCase().includes('begehung'))),
    );
    if (inspectionServices.length === 0) return null;
    return inspectionServices.reduce((prev, current) => (prev.quantityContracted > current.quantityContracted) ? prev : current);
};
